import { allocate, includedTax, splitTax, type SupplyType, type TaxBreakdown, type TaxRow } from './tax.js';

/**
 * Everything a GST tax invoice says, worked out from an order that is already in the database. Pure: the PDF only draws what
 * this returns, so the figures can be checked without making a PDF.
 *
 * Prices include GST, so a line is a price with tax inside it: the taxable value is the price without the tax. The tax of
 * each rate is the figure recorded on the order (taxBreakdown), shared out over that rate's lines to the paisa, so the lines
 * always add up to the order's tax and to its total.
 */

export interface InvoiceOrder {
  number: string;
  placedAt: Date | string;
  currency?: string;
  subtotal: number;
  discountTotal: number;
  paymentDiscount: number;
  shippingTotal: number;
  taxTotal: number;
  grandTotal: number;
  /** null on orders placed before prices included GST: for those, tax was added on top of the subtotal */
  taxBreakdown: TaxBreakdown | null;
  items: {
    productName: string;
    sku: string;
    hsnCode: string | null;
    quantity: number;
    unitPrice: number;
    /** what the line cost after its share of the discounts */
    lineTotal: number;
    taxAmount: number;
    discountAmount: number;
    variantAttributes: unknown;
  }[];
}

export interface InvoiceLine {
  kind: 'item' | 'shipping';
  description: string;
  /** the variant ("Scent: Vanilla"), or the SKU */
  detail: string;
  hsn: string;
  quantity: number;
  /** one unit at its list price, GST included */
  unitPrice: number;
  /** quantity x unit price, before any discount */
  listTotal: number;
  discount: number;
  /** the price without GST */
  taxable: number;
  rateBps: number;
  cgst: number;
  sgst: number;
  igst: number;
  tax: number;
  /** what the customer pays for the line */
  total: number;
}

export interface InvoiceModel {
  lines: InvoiceLine[];
  /** one row per GST rate, as recorded on the order */
  summary: TaxRow[];
  supply: SupplyType;
  /** false for an old order whose tax was added on top of its prices */
  taxIncluded: boolean;
  totals: {
    /** the goods at list price, GST included */
    listTotal: number;
    couponDiscount: number;
    onlineDiscount: number;
    shipping: number;
    taxable: number;
    cgst: number;
    sgst: number;
    igst: number;
    tax: number;
    grand: number;
  };
}

/** "SKU-1 (Scent: Vanilla)" -> "Scent: Vanilla"; with no variant, the SKU. */
function detailOf(item: InvoiceOrder['items'][number]): string {
  const attrs = item.variantAttributes && typeof item.variantAttributes === 'object' ? Object.entries(item.variantAttributes as Record<string, unknown>) : [];
  const text = attrs.filter(([, v]) => typeof v === 'string' && v).map(([k, v]) => `${k}: ${v}`).join(', ');
  return text || item.sku;
}

/** The rate (from those on the order) that best explains how much tax a price carries. */
export function inferRate(gross: number, tax: number, rates: number[]): number {
  if (rates.length === 0) return 0;
  let best = rates[0]!;
  let bestGap = Infinity;
  for (const r of rates) {
    const gap = Math.abs(includedTax(gross, r) - tax);
    if (gap < bestGap || (gap === bestGap && r > best && tax > 0)) {
      best = r;
      bestGap = gap;
    }
  }
  return best;
}

export function buildInvoice(order: InvoiceOrder): InvoiceModel {
  const rows = order.taxBreakdown?.rows ?? [];
  const rates = rows.map((r) => r.rateBps);
  const supply: SupplyType = order.taxBreakdown?.supply ?? 'inter';
  const taxIncluded = order.taxBreakdown !== null;

  const itemTax = order.items.reduce((s, i) => s + i.taxAmount, 0);
  const shippingTax = Math.max(0, order.taxTotal - itemTax);

  const base = [
    ...order.items.map((i) => ({
      kind: 'item' as const,
      description: i.productName,
      detail: detailOf(i),
      hsn: i.hsnCode ?? '',
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      listTotal: i.unitPrice * i.quantity,
      discount: i.discountAmount,
      gross: i.lineTotal,
      recordedTax: i.taxAmount,
    })),
    ...(order.shippingTotal > 0
      ? [{ kind: 'shipping' as const, description: 'Shipping charges', detail: 'Courier service', hsn: '996812', quantity: 1, unitPrice: order.shippingTotal, listTotal: order.shippingTotal, discount: 0, gross: order.shippingTotal, recordedTax: shippingTax }]
      : []),
  ];

  let lines: InvoiceLine[];
  if (taxIncluded) {
    const rateOf = base.map((b) => inferRate(b.gross, b.recordedTax, rates));
    // each rate's tax is the order's recorded figure for it, shared over that rate's lines
    // and so is its CGST (the rest of each line's tax is its SGST), so the columns add up to the order's own CGST and SGST
    const tax = new Array<number>(base.length).fill(0);
    const cgst = new Array<number>(base.length).fill(0);
    for (const row of rows) {
      const idx = base.map((_, i) => i).filter((i) => rateOf[i] === row.rateBps);
      allocate(row.tax, idx.map((i) => base[i]!.gross)).forEach((share, n) => (tax[idx[n]!] = share));
      allocate(row.cgst, idx.map((i) => tax[i]!)).forEach((share, n) => (cgst[idx[n]!] = share));
    }
    lines = base.map((b, i) => {
      const t = tax[i]!;
      const split = supply === 'inter' ? splitTax(t, 'inter') : { cgst: cgst[i]!, sgst: t - cgst[i]!, igst: 0 };
      return { kind: b.kind, description: b.description, detail: b.detail, hsn: b.hsn, quantity: b.quantity, unitPrice: b.unitPrice, listTotal: b.listTotal, discount: b.discount, taxable: b.gross - t, rateBps: rateOf[i]!, ...split, tax: t, total: b.gross };
    });
  } else {
    // an old order: tax was added on top, so the line is its price plus its tax
    lines = base.map((b) => ({ kind: b.kind, description: b.description, detail: b.detail, hsn: b.hsn, quantity: b.quantity, unitPrice: b.unitPrice, listTotal: b.listTotal, discount: b.discount, taxable: b.gross, rateBps: 0, cgst: 0, sgst: 0, igst: 0, tax: b.recordedTax, total: b.gross + b.recordedTax }));
  }

  const sum = (pick: (l: InvoiceLine) => number) => lines.reduce((s, l) => s + pick(l), 0);
  return {
    lines,
    summary: rows,
    supply,
    taxIncluded,
    totals: {
      listTotal: order.subtotal,
      couponDiscount: Math.max(0, order.discountTotal - order.paymentDiscount),
      onlineDiscount: order.paymentDiscount,
      shipping: order.shippingTotal,
      taxable: sum((l) => l.taxable),
      cgst: sum((l) => l.cgst),
      sgst: sum((l) => l.sgst),
      igst: sum((l) => l.igst),
      tax: sum((l) => l.tax),
      grand: order.grandTotal,
    },
  };
}

// ----------------------------------------------------------------------------------- in words --

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const belowThousand = (n: number): string => {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const tens = rest < 20 ? ONES[rest]! : `${TENS[Math.floor(rest / 10)]}${rest % 10 ? ` ${ONES[rest % 10]}` : ''}`;
  return [hundreds ? `${ONES[hundreds]} Hundred` : '', tens].filter(Boolean).join(' ');
};

/** 123456789 -> "Twelve Crore Thirty Four Lakh Fifty Six Thousand Seven Hundred Eighty Nine" (the Indian way of grouping). */
export function numberInWords(n: number): string {
  if (n === 0) return 'Zero';
  const parts: string[] = [];
  let rest = Math.floor(n);
  const crore = Math.floor(rest / 10_000_000);
  rest %= 10_000_000;
  const lakh = Math.floor(rest / 100_000);
  rest %= 100_000;
  const thousand = Math.floor(rest / 1000);
  rest %= 1000;
  if (crore) parts.push(`${numberInWords(crore)} Crore`);
  if (lakh) parts.push(`${belowThousand(lakh)} Lakh`);
  if (thousand) parts.push(`${belowThousand(thousand)} Thousand`);
  if (rest) parts.push(belowThousand(rest));
  return parts.join(' ');
}

/** 123450 (paise) -> "Rupees One Thousand Two Hundred Thirty Four and Fifty Paise Only" */
export function amountInWords(paise: number): string {
  const rupees = Math.floor(Math.abs(paise) / 100);
  const rest = Math.abs(paise) % 100;
  const words = `Rupees ${numberInWords(rupees)}${rest ? ` and ${numberInWords(rest)} Paise` : ''} Only`;
  return paise < 0 ? `Minus ${words}` : words;
}

// ---------------------------------------------------------------------------------- numbering --

/** India's financial year runs April to March: 19 Oct 2026 -> "2026-27", 15 Feb 2027 -> "2026-27". Takes the India date. */
export function financialYear(date: Date): string {
  const local = new Date(date.getTime() + 5.5 * 3_600_000);
  const year = local.getUTCFullYear();
  const start = local.getUTCMonth() >= 3 ? year : year - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

/** "MS/2026-27/00012": a prefix, the financial year and a running number that restarts each year (GST wants one unbroken series). */
export const formatInvoiceNumber = (prefix: string, fy: string, n: number) => `${prefix}/${fy}/${String(n).padStart(5, '0')}`;

/**
 * A GST invoice number is at most 16 characters (rule 46). "MS/2026-27/00012" is exactly 16, so the prefix is one or two
 * letters or digits.
 */
export const INVOICE_PREFIX_PATTERN = /^[A-Z0-9]{1,2}$/;
